import React, { useEffect, useState } from 'react'
import {
    BulkActionDialog,
    BulkActionDialogActions,
    BulkActionField,
    bulkActionInputClassName,
} from '../../components/ui/BulkActionDialog'
import { customFieldApiError, customFieldsApi } from './api'
import type { CustomFieldDefinition } from './types'

export interface CustomFieldEditModalProps {
    isOpen: boolean
    definition: CustomFieldDefinition | null
    profileId: string
    onClose: () => void
    onSaved: () => void
}

export const CustomFieldEditModal = ({
    isOpen,
    definition,
    profileId,
    onClose,
    onSaved,
}: CustomFieldEditModalProps) => {
    const [name, setName] = useState('')
    const [description, setDescription] = useState('')
    const [error, setError] = useState<string | null>(null)
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        if (!isOpen || !definition) return
        setName(definition.name)
        setDescription(definition.description ?? '')
        setError(null)
    }, [isOpen, definition])

    if (!definition) return null

    const submit = async () => {
        if (!name.trim()) return setError('Name is required')
        setSaving(true)
        setError(null)
        try {
            await customFieldsApi.updateDefinition(profileId, {
                ...definition,
                name: name.trim(),
                description: description.trim() || null,
            })
            onSaved()
            onClose()
        } catch (requestError) {
            setError(customFieldApiError(requestError))
        } finally {
            setSaving(false)
        }
    }

    return (
        <BulkActionDialog
            isOpen={isOpen}
            onClose={onClose}
            title={`Edit ${definition.key}`}
            width="compact"
            dismissible={!saving}
            footer={
                <BulkActionDialogActions
                    primaryLabel="Save"
                    loadingLabel="Saving…"
                    loading={saving}
                    disabled={!name.trim()}
                    onPrimary={submit}
                    onCancel={onClose}
                />
            }
        >
            <BulkActionField label="Name" htmlFor="custom-field-edit-name" required error={error}>
                <input
                    id="custom-field-edit-name"
                    className={bulkActionInputClassName}
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    maxLength={100}
                />
            </BulkActionField>
            <BulkActionField label="Description" htmlFor="custom-field-edit-description">
                <input
                    id="custom-field-edit-description"
                    className={bulkActionInputClassName}
                    value={description}
                    onChange={(event) => setDescription(event.target.value)}
                    maxLength={1000}
                    placeholder="Description (optional)"
                />
            </BulkActionField>
        </BulkActionDialog>
    )
}
